import { NavLink, matchPath, useLocation } from "react-router";

import { NotificationIcon } from "@citrine/atoms/icons";
import { Spacer } from "@citrine/atoms/spacer";
import type { DataFullstoryId } from "@citrine/types/full-story";

import { Logo } from "#src/components/logo";

import { RoutedNavbarTeamInfo } from "../team-info";

import * as sx from "./navbar.sx";
import { RoutedProjectToolbar } from "./project-toolbar";
import { VersionInfo } from "./version-info";

export interface NavRoute {
  "data-fullstoryid"?: DataFullstoryId;
  path: string;
  label: string;
  notification?: boolean;
}

interface NavbarShellProps {
  logo?: {
    alt?: string;
    src?: string;
  };
  children?: React.ReactNode;
}

interface NavbarProps extends NavbarShellProps {
  routes: NavRoute[];
}

export function NavbarShell({ children, logo }: NavbarShellProps) {
  return (
    <sx.Toolbar>
      <sx.Logo to="/">
        <Logo {...logo} />
      </sx.Logo>
      {children}
    </sx.Toolbar>
  );
}

function NavToolbar({ children, logo, routes }: NavbarProps) {
  const loc = useLocation();
  const matchedRoute = routes
    .map((route) =>
      matchPath(route.path.replace(/(^|[^/])\/*$/, "$1/*"), loc.pathname)
    )
    .find(Boolean);

  return (
    <NavbarShell logo={logo}>
      <VersionInfo />
      <RoutedNavbarTeamInfo />
      {/* use negative margin to avoid a double gap in the flex row */}
      <Spacer sx={{ marginLeft: -4 }} />
      <sx.Tabs value={matchedRoute?.pathnameBase ?? false}>
        {routes.map((route: NavRoute) => (
          <sx.Tab
            data-fullstoryid={route["data-fullstoryid"]}
            disableRipple
            key={route.path}
            value={route.path}
            label={
              <sx.Label>
                {route.notification ? (
                  <NotificationIcon fontSize="small" />
                ) : null}
                {route.label}
              </sx.Label>
            }
            component={NavLink}
            to={route.path}
          />
        ))}
      </sx.Tabs>
      {children}
    </NavbarShell>
  );
}

export function Navbar(props: NavbarProps) {
  return (
    <>
      <sx.Navbar role="banner">
        <NavToolbar {...props} />
      </sx.Navbar>
      <sx.ProjectToolbar position="static" role="navigation">
        <RoutedProjectToolbar />
      </sx.ProjectToolbar>
    </>
  );
}
